/**
 * TeachingScreen — Explains the meaning of each bell in the active retreat.
 * "What does that bell mean?"
 *
 * UI-INV-005: Leaf component, props only, no engine imports.
 * Always guards against empty/undefined bells — never crashes.
 */
import { MetaStrip } from "../components/MetaStrip";
import styles from "./TeachingScreen.module.css";
import { TEST_IDS } from "../testing/testIds";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { SecondaryAction } from "../primitives/SecondaryAction";

export type { TeachingBell } from "../data/teachingBells";
import type { TeachingBell } from "../data/teachingBells";

type Props = {
  bells: TeachingBell[];
  traditionLabel: string;
  onBack: () => void;
  onDone: () => void;
  onPlayBell: (bellId: string) => void;
};

export function TeachingScreen({ bells, traditionLabel, onBack, onDone, onPlayBell }: Props) {
  const safeBells = bells ?? [];

  return (
    <div className={styles.screen} data-testid={TEST_IDS.TEACHING_SCREEN}>
      <MetaStrip label="Teaching" onBack={onBack} />

      <div className={styles.content}>
        {/* Header */}
        <p className={styles.eyebrow}>The signals · {traditionLabel || "tradition"}</p>
        <h1 className={styles.heading}>The bells, and what they mean.</h1>
        <p className={styles.subheading}>
          These are not random sounds. Each marks a moment in the day. You'll come to know them.
        </p>

        {/* Bell cards */}
        {safeBells.length === 0 ? (
          <div className={styles.empty} data-testid={TEST_IDS.TEACHING_EMPTY}>
            <p className={styles.emptyText}>No bells are defined for this retreat.</p>
          </div>
        ) : (
          <div className={styles.bellCards}>
            {safeBells.map((bell) => (
              <div key={bell.id} className={styles.bellCard} data-testid={TEST_IDS.TEACHING_BELL_CARD}>
                <button
                  className={styles.playBtn}
                  onClick={() => onPlayBell(bell.id)}
                  type="button"
                  aria-label={`Play ${bell.name}`}
                  data-testid={TEST_IDS.TEACHING_PLAY_BTN}
                  data-bell-id={bell.id}
                >
                  <span className={styles.playIcon} aria-hidden="true">▶</span>
                </button>
                <div className={styles.bellInfo}>
                  <p className={styles.bellName}>{bell.name}</p>
                  <p className={styles.bellMeta}>{bell.tradition} · {bell.role}</p>
                  <p className={styles.bellTeaches}>{bell.teaches}</p>
                </div>
              </div>
            ))}
          </div>
        )}


        {/* Footer card */}
        <div className={styles.footerCard}>
          <p className={styles.footerText}>
            You won't be quizzed. The body remembers what the schedule teaches it.
          </p>
        </div>
      </div>

      {/* CTAs */}
      <div className={styles.ctas}>
        <PrimaryAction onClick={onDone} testId={TEST_IDS.TEACHING_DONE_BTN}>I've heard them all</PrimaryAction>
        <SecondaryAction onClick={onDone} testId={TEST_IDS.TEACHING_SKIP_BTN}>Skip · I'll learn them as they come</SecondaryAction>
      </div>
    </div>
  );
}
